import { Warning } from '@carbon/react/icons';
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
} from '@carbon/react';
import { Date } from 'components/date';
import { TitleEntityAsset } from 'components/map/types';
import style from './entity-title-assets.module.scss';
import { useMemo } from 'react';
import { format10 } from '../../utils/number';

const NULL_DISPLAY_VALUE = '--';

// Component for displaying entity title assets in a popup
export const EntityTitleAssets = ({
  assets,
  activeTab,
}: {
  assets: TitleEntityAsset[];
  activeTab: string;
}) => {
  // Filter assets based on the selected interest type
  const filteredAssets = useMemo(() => {
    if (!activeTab) {
      return [];
    }
    return assets.filter((asset) => asset.interestType === activeTab);
  }, [assets, activeTab]);

  const tractNameMaxLimit = 25; // Smaller for popups

  return (
    <div className={style.entityTitleAssets}>
      <TableContainer className={style.assetTable}>
        <Table
          aria-label="Entity Assets"
          size="sm"
          className={style.fixedHeader}
        >
          <TableHead className={style.stickyHeader}>
            <TableRow>
              <TableHeader>Workspace</TableHeader>
              <TableHeader>Sub-Tract Name</TableHeader>
              <TableHeader>Last Calculated</TableHeader>
              <TableHeader>Surface</TableHeader>
              <TableHeader>Start Depth</TableHeader>
              <TableHeader>End Depth (ft)</TableHeader>
              <TableHeader>Royalty Rate</TableHeader>
              <TableHeader>Executive</TableHeader>
              <TableHeader>Bonus</TableHeader>
              <TableHeader>Delay</TableHeader>
              <TableHeader>Royalty</TableHeader>
              <TableHeader>Fixed</TableHeader>
              <TableHeader>WI</TableHeader>
              <TableHeader>ORR</TableHeader>
              <TableHeader>NRI</TableHeader>
              <TableHeader>Gross Acreage</TableHeader>
              <TableHeader>Legal Acreage</TableHeader>
              <TableHeader>Lease</TableHeader>
              <TableHeader>County</TableHeader>
              <TableHeader>Burdened Entity</TableHeader>
              <TableHeader>Warnings</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAssets?.length ? (
              filteredAssets.map((asset, i) => (
                <TableRow key={`popup-asset-${i}`}>
                  <TableCell className={style.assetsColumn}>
                    {asset.titleWorkspace?.name ? (
                      <Link
                        href={asset.titleWorkspace?.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={style.visibleLink}
                      >
                        {asset.titleWorkspace?.name}
                      </Link>
                    ) : (
                      NULL_DISPLAY_VALUE
                    )}
                  </TableCell>
                  <TableCell className={style.trackNameColumn}>
                    {asset.coveredArea?.name ? (
                      asset.coveredArea.name.length > tractNameMaxLimit ? (
                        <Tooltip
                          label={asset.coveredArea.name}
                          align="top-start"
                        >
                          <span tabIndex={0}>
                            {asset.coveredArea.name.slice(
                              0,
                              tractNameMaxLimit - 3
                            ) + '...'}
                          </span>
                        </Tooltip>
                      ) : (
                        <p>{asset.coveredArea.name}</p>
                      )
                    ) : (
                      NULL_DISPLAY_VALUE
                    )}
                  </TableCell>
                  <TableCell>
                    {asset.titleWorkspace?.lastCalculated ? (
                      <Date date={asset.titleWorkspace.lastCalculated} />
                    ) : (
                      NULL_DISPLAY_VALUE
                    )}
                  </TableCell>
                  <TableCell>{asset.surface || '0.00000000'}</TableCell>
                  <TableCell>
                    {asset.coveredArea?.startDepthInFeet || '0.0'}
                  </TableCell>
                  <TableCell>
                    {asset.coveredArea?.endDepthInFeet || '∞'}
                  </TableCell>
                  <TableCell>
                    {asset.lease?.royaltyRate || NULL_DISPLAY_VALUE}
                  </TableCell>
                  <TableCell>{format10(asset.executive)}</TableCell>
                  <TableCell>{format10(asset.bonus)}</TableCell>
                  <TableCell>{format10(asset.delay)}</TableCell>
                  <TableCell>{format10(asset.royalty)}</TableCell>
                  <TableCell>{format10(asset.fixedRoyalty)}</TableCell>
                  <TableCell>{format10(asset.working)}</TableCell>
                  <TableCell>{format10(asset.override)}</TableCell>
                  <TableCell>{format10(asset.netRevenueInterest)}</TableCell>
                  <TableCell>
                    {asset.coveredArea?.calculatedAcreage || NULL_DISPLAY_VALUE}
                  </TableCell>
                  <TableCell>
                    {asset.coveredArea?.legalAcreage || NULL_DISPLAY_VALUE}
                  </TableCell>
                  <TableCell className={style.leaseColumn}>
                    {asset.lease?.name || NULL_DISPLAY_VALUE}
                  </TableCell>
                  <TableCell>
                    {asset.titleWorkspace?.referenceLocation?.county?.name ||
                      NULL_DISPLAY_VALUE}
                  </TableCell>
                  <TableCell>
                    {asset.burdenedEntity ? (
                      <Link href={asset.burdenedEntity.href} target="_blank">
                        {asset.burdenedEntity.name}
                      </Link>
                    ) : (
                      NULL_DISPLAY_VALUE
                    )}
                  </TableCell>
                  <TableCell>
                    {asset.preventedOverconveyance ? (
                      <Warning />
                    ) : (
                      NULL_DISPLAY_VALUE
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={15}>No assets available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
